import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Target } from "@phosphor-icons/react";
import { API } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  async function submit(e) {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/creator/reset-password`, { token, password });
      toast.success("Password updated. You can now sign in.");
      nav("/creator/login");
    } catch (err) {
      let d = err?.response?.data?.detail;
      if (typeof d !== "string") d = null;
      toast.error(d || "Could not reset password. The link may have expired.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="grain absolute inset-0 pointer-events-none" />
      <div className="w-full max-w-md relative">
        <Link to="/creator/login" className="kbd-label text-white/50 hover:text-neon-cyan">← BACK TO SIGN IN</Link>
        <div className="card-tactical p-8 mt-4">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-neon-yellow flex items-center justify-center">
              <Target size={22} weight="bold" color="#050505" />
            </div>
            <div>
              <div className="kbd-label text-neon-yellow">CREATOR PORTAL</div>
              <h1 className="display-heading font-black text-2xl">Reset Password</h1>
            </div>
          </div>

          {!token ? (
            <div data-testid="reset-no-token" className="space-y-4">
              <p className="text-white/80 text-sm leading-relaxed">
                This reset link is missing or invalid. Please request a new one.
              </p>
              <Link to="/creator/forgot-password" data-testid="reset-request-new-btn" className="neon-btn w-full py-3 inline-flex items-center justify-center">
                REQUEST NEW LINK
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <div className="kbd-label mb-2">NEW PASSWORD</div>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="reset-password-input"
                  className="w-full bg-black/60 border border-white/10 px-4 py-3 font-mono text-sm focus:outline-none focus:border-neon-cyan"
                  placeholder="At least 8 characters"
                />
              </div>
              <div>
                <div className="kbd-label mb-2">CONFIRM PASSWORD</div>
                <input
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-confirm-input"
                  className="w-full bg-black/60 border border-white/10 px-4 py-3 font-mono text-sm focus:outline-none focus:border-neon-cyan"
                  placeholder="Re-enter password"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="reset-submit-btn"
                className="neon-btn w-full py-3 mt-2 disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {busy ? "UPDATING…" : "UPDATE PASSWORD"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
